"use client";

import {
  useCallback,
  useImperativeHandle,
  type ForwardedRef,
  type RefObject,
} from "react";
import type { CteamscarouselRef } from "./types";

interface UseCarouselImperativeScrollInput {
  containerRef: RefObject<HTMLDivElement | null>;
  pitch: number;
  ref: ForwardedRef<CteamscarouselRef>;
}

export function useCarouselImperativeScroll({
  containerRef,
  pitch,
  ref,
}: UseCarouselImperativeScrollInput): (doubledIndex: number) => void {
  const handleScrollToIndex = useCallback(
    (doubledIndex: number): void => {
      const scrollElement = containerRef.current;
      if (!scrollElement) return;

      scrollElement.scrollTo({ left: doubledIndex * pitch, behavior: "smooth" });
    },
    [containerRef, pitch],
  );
  const scrollByPitches = useCallback(
    (delta: number): void => {
      const scrollElement = containerRef.current;
      if (!scrollElement) return;

      const baseIndex = Math.round(scrollElement.scrollLeft / pitch);
      scrollElement.scrollTo({
        left: (baseIndex + delta) * pitch,
        behavior: "smooth",
      });
    },
    [containerRef, pitch],
  );
  const scrollToPrevious = useCallback(
    (): void => scrollByPitches(-1),
    [scrollByPitches],
  );
  const scrollToNext = useCallback(
    (): void => scrollByPitches(1),
    [scrollByPitches],
  );

  useImperativeHandle(ref, () => ({ scrollToPrevious, scrollToNext }), [
    scrollToPrevious,
    scrollToNext,
  ]);

  return handleScrollToIndex;
}
